// Single source of truth for runtime theme state. The inline
// resolver in src/index.html handles INITIAL paint to avoid FOUC;
// this module handles RUNTIME toggling. Both must agree on:
//   - Storage key:    'bgw:theme'
//   - Pref → theme:   'light' → 'breathe', 'dark' → 'breathe-dark'
//   - Attribute:      <html> data-theme + color-scheme

const STORAGE_KEY = 'bgw:theme';
type ThemePref = 'light' | 'dark';

function themeNameFor(pref: ThemePref): 'breathe' | 'breathe-dark' {
  return pref === 'dark' ? 'breathe-dark' : 'breathe';
}

function applyTheme(pref: ThemePref): void {
  document.documentElement.setAttribute('data-theme', themeNameFor(pref));
  document.documentElement.style.colorScheme = pref;
}

function readStoredPref(): ThemePref | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function systemPref(): ThemePref {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getTheme(): ThemePref {
  return readStoredPref() ?? systemPref();
}

export function setTheme(pref: ThemePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch { /* private mode etc. — apply anyway */ }
  applyTheme(pref);
}

/**
 * Binds click handlers to any `[data-theme-toggle]` element in
 * the DOM. Idempotent — safe to call multiple times.
 */
export function setupThemeToggle(): void {
  const buttons = document.querySelectorAll<HTMLElement>('[data-theme-toggle]');
  for (const btn of buttons) {
    if (btn.dataset.bound === '1') continue;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const next = getTheme() === 'dark' ? 'light' : 'dark';
      setTheme(next);
      updateButtonState(btn);
    });
    updateButtonState(btn);
  }
}

function updateButtonState(btn: HTMLElement): void {
  const current = getTheme();
  const next = current === 'dark' ? 'light' : 'dark';
  btn.setAttribute('aria-label', `Switch to ${next} theme`);
  btn.setAttribute('aria-pressed', current === 'dark' ? 'true' : 'false');
}
